import React from "react";
import styles from "./CsvMetadata.module.css";

// Same reading as the old logs page: "Smoothness" is the legacy key from before
// the scoring revamp, kept last so the newer keys win when both appear.
const SCORE_KEYS = ["JerkScore", "TwistJerk", "ZeroCross", "Accuracy", "Smoothness"] as const;
const SCORE_META: Record<(typeof SCORE_KEYS)[number], { label: string; hint: string }> = {
  JerkScore: { label: "Jerk Score", hint: "lower = smoother" },
  TwistJerk: { label: "Twist Jerk", hint: "lower = smoother" },
  ZeroCross: { label: "Zero-Cross", hint: "lower = steadier" },
  Accuracy: { label: "Accuracy", hint: "higher = better" },
  Smoothness: { label: "Smoothness", hint: "legacy" },
};
const KNOWN = new Set(["Name", "Workout", "Date HR", "Date MS", "Duration", "Reps", "Hand", "Interrupted", ...SCORE_KEYS]);

const num = (raw: string | undefined, digits: number): string | null => {
  const n = parseFloat(raw ?? "");
  return Number.isFinite(n) ? n.toFixed(digits) : null;
};

function recordedAt(ms: string | undefined): string | null {
  const n = parseInt(ms ?? "", 10);
  if (!n) return null;
  return new Date(n).toLocaleString(undefined, {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** The CSV's first line (workout, timing, scores per rep, app version...) as a readable panel. */
export default function CsvMetadata({ metadata }: { metadata: Record<string, string> | null | undefined }) {
  const raw = metadata || {};
  if (Object.keys(raw).length === 0) return null;

  const scoreKey = SCORE_KEYS.find((k) => raw[k] !== undefined) || null;
  const reps = parseInt(raw.Reps || "0", 10) || 0;
  const perRep = Array.from({ length: reps }, (_, i) => ({
    rep: i + 1,
    duration: num(raw[`Durations#${i + 1}`], 2),
    score: scoreKey ? num(raw[`${scoreKey}#${i + 1}`], 2) : null,
  }));
  const hidden = new Set(KNOWN);
  perRep.forEach(({ rep }) => {
    hidden.add(`Durations#${rep}`);
    if (scoreKey) hidden.add(`${scoreKey}#${rep}`);
  });
  // "Date HR" contains a comma, so the CSV parser splits it into fragments like "2026 10": drop them.
  const extra = Object.entries(raw).filter(([k]) => !hidden.has(k) && !/^\w+ \d+$/.test(k) && !/^\d{4} \d{2}$/.test(k));
  const duration = num(raw.Duration, 2);
  const when = recordedAt(raw["Date MS"]);
  const score = scoreKey ? num(raw[scoreKey], 2) : null;

  return (
    <div className={styles.panel}>
      <div className={styles.facts}>
        {raw.Workout && <strong>{raw.Workout}</strong>}
        {when && <span>{when} (New York)</span>}
        {raw.Hand && <span>{raw.Hand} hand</span>}
        <span>{reps} {reps === 1 ? "rep" : "reps"}</span>
        {duration && <span>{duration} s</span>}
        {raw.Interrupted === "true" && <span className="ui-badge ui-badge--warning">Interrupted</span>}
      </div>
      {scoreKey && (
        <p className={styles.score}>
          {SCORE_META[scoreKey].label} <span className={styles.hint}>({SCORE_META[scoreKey].hint})</span>:{" "}
          <strong>{score ?? "—"}</strong>
        </p>
      )}
      {perRep.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Rep</th>
              <th>Duration</th>
              {scoreKey && <th>{SCORE_META[scoreKey].label}</th>}
            </tr>
          </thead>
          <tbody>
            {perRep.map((r) => (
              <tr key={r.rep}>
                <td>{r.rep}</td>
                <td>{r.duration ? `${r.duration} s` : "—"}</td>
                {scoreKey && <td>{r.score ?? "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {extra.length > 0 && (
        <dl className={styles.extra}>
          {extra.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v || "—"}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}
